import React, { useState, useEffect, useCallback } from 'react';
import { PublicKey, LAMPORTS_PER_SOL, SystemProgram } from '@solana/web3.js';
import { BN } from '@coral-xyz/anchor';

// Card utilities
const SUITS = ['♠', '♥', '♦', '♣'];
const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

function getCardDisplay(cardIndex) {
  if (cardIndex === 0) return '?';
  const rank = RANKS[(cardIndex - 1) % 13];
  const suit = SUITS[Math.floor((cardIndex - 1) / 13)];
  return `${rank}${suit}`;
}

function isRed(cardIndex) {
  if (cardIndex === 0) return false;
  const suit = Math.floor((cardIndex - 1) / 13);
  return suit === 1 || suit === 2;
}

// Generate random hand (for demo - in production this comes from TEE)
function generateHand() {
  const cards = [];
  while (cards.length < 2) {
    const card = Math.floor(Math.random() * 52) + 1;
    if (!cards.includes(card)) cards.push(card);
  }
  return cards;
}

function PokerTable({ program, wallet, connection, game, onLeave, onRefresh }) {
  const [gameData, setGameData] = useState(game);
  const [myHand, setMyHand] = useState([]);
  const [betAmount, setBetAmount] = useState('0.05');
  const [loading, setLoading] = useState(false);
  const [committed, setCommitted] = useState(false);

  // Refresh game data
  const refreshGame = useCallback(async () => {
    if (!program || !game.publicKey) return;
    try {
      const data = await program.account.game.fetch(game.publicKey);
      setGameData({ publicKey: game.publicKey, ...data });
    } catch (e) {
      console.error('Error fetching game:', e);
    }
  }, [program, game.publicKey]);

  useEffect(() => {
    refreshGame();
    const interval = setInterval(refreshGame, 3000);
    return () => clearInterval(interval);
  }, [refreshGame]);

  // Deal cards (client-side for demo)
  useEffect(() => {
    if (myHand.length === 0) {
      setMyHand(generateHand());
    }
  }, []);

  // Commit hand
  const commitHand = async () => {
    if (!program || !wallet.publicKey) return;
    setLoading(true);
    
    try {
      // Create hash of hand (simplified - in production use proper crypto)
      const handHash = new Uint8Array(32);
      handHash[0] = myHand[0];
      handHash[1] = myHand[1];
      
      const [choicePda] = PublicKey.findProgramAddressSync(
        [Buffer.from('choice'), game.publicKey.toBuffer(), wallet.publicKey.toBuffer()],
        program.programId
      );

      const tx = await program.methods
        .commitHand(Array.from(handHash))
        .accounts({
          game: game.publicKey,
          playerChoice: choicePda,
          player: wallet.publicKey,
          systemProgram: SystemProgram.programId,
        })
        .rpc();

      console.log('Hand committed:', tx);
      setCommitted(true);
      await refreshGame();
    } catch (e) {
      console.error('Error committing hand:', e);
      alert('Failed to commit hand: ' + e.message);
    }
    setLoading(false);
  };

  // Place bet
  const placeBet = async () => {
    if (!program || !wallet.publicKey) return;
    setLoading(true);
    
    try {
      const [choicePda] = PublicKey.findProgramAddressSync(
        [Buffer.from('choice'), game.publicKey.toBuffer(), wallet.publicKey.toBuffer()],
        program.programId
      );

      const amount = new BN(parseFloat(betAmount) * LAMPORTS_PER_SOL);

      const tx = await program.methods
        .placeBet(amount)
        .accounts({
          game: game.publicKey,
          playerChoice: choicePda,
          player: wallet.publicKey,
          systemProgram: SystemProgram.programId,
        })
        .rpc();

      console.log('Bet placed:', tx);
      await refreshGame();
      onRefresh();
    } catch (e) {
      console.error('Error placing bet:', e);
      alert('Failed to place bet: ' + e.message);
    }
    setLoading(false);
  };

  // Fold
  const fold = async () => {
    if (!program || !wallet.publicKey) return;
    setLoading(true);
    
    try {
      const [choicePda] = PublicKey.findProgramAddressSync(
        [Buffer.from('choice'), game.publicKey.toBuffer(), wallet.publicKey.toBuffer()],
        program.programId
      );

      const tx = await program.methods
        .fold()
        .accounts({
          game: game.publicKey,
          playerChoice: choicePda,
          player: wallet.publicKey,
        })
        .rpc();

      console.log('Folded:', tx);
      await refreshGame();
    } catch (e) {
      console.error('Error folding:', e);
      alert('Failed to fold: ' + e.message);
    }
    setLoading(false);
  };

  // Claim winnings
  const claimWinnings = async () => {
    if (!program || !wallet.publicKey) return;
    setLoading(true);
    
    try {
      const tx = await program.methods
        .claimWinnings()
        .accounts({
          game: game.publicKey,
          winner: wallet.publicKey,
        })
        .rpc();

      console.log('Winnings claimed:', tx);
      await refreshGame();
      onRefresh();
    } catch (e) {
      console.error('Error claiming:', e);
      alert('Failed to claim: ' + e.message);
    }
    setLoading(false);
  };

  const isPlayer1 = gameData.player1?.toString() === wallet.publicKey?.toString();
  const isWinner = gameData.winner?.toString() === wallet.publicKey?.toString();
  const pot = gameData.pot?.toNumber() / LAMPORTS_PER_SOL || 0;
  const communityCards = gameData.communityCards || [0, 0, 0, 0, 0];
  const revealed = gameData.communityRevealed || 0;

  const getStatus = () => {
    if (gameData.status?.waitingForPlayer) return 'Waiting for opponent...';
    if (gameData.status?.dealing) return 'Dealing cards...';
    if (gameData.status?.betting) return 'Betting round';
    if (gameData.status?.showdown) return 'Showdown!';
    if (gameData.status?.finished) return gameData.winner ? 'Game Over' : 'Game Finished';
    return 'Unknown';
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={onLeave} style={styles.backBtn}>← Leave Game</button>
        <h2>Game #{gameData.gameId?.toString().slice(-6)}</h2>
        <span style={styles.status}>{getStatus()}</span>
      </div>

      {/* Table */}
      <div style={styles.table}>
        {/* Opponent */}
        <div style={styles.opponent}>
          <div style={styles.playerLabel}>
            {isPlayer1 ? 'Opponent' : 'Creator'}
            {!isPlayer1 && gameData.player1 && (
              <span style={styles.addr}>{gameData.player1.toString().slice(0,8)}...</span>
            )}
            {isPlayer1 && gameData.player2 && (
              <span style={styles.addr}>{gameData.player2.toString().slice(0,8)}...</span>
            )}
          </div>
          <div style={styles.cards}>
            <div style={styles.cardBack}>?</div>
            <div style={styles.cardBack}>?</div>
          </div>
        </div>

        {/* Community Cards */}
        <div style={styles.community}>
          <div style={styles.communityLabel}>Community Cards</div>
          <div style={styles.communityCards}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div 
                key={i} 
                style={{
                  ...styles.card,
                  ...(i < revealed && communityCards[i] > 0 ? 
                    (isRed(communityCards[i]) ? styles.redCard : {}) : 
                    styles.cardBack),
                }}
              >
                {i < revealed ? getCardDisplay(communityCards[i]) : ''}
              </div>
            ))}
          </div>
        </div>

        {/* Pot */}
        <div style={styles.pot}>
          <span style={styles.potLabel}>POT</span>
          <span style={styles.potAmount}>{pot.toFixed(4)} SOL</span>
          {pot > 0 && (
            <div style={styles.burnLine}>
              <span style={styles.burnFireSmall}>🔥</span>
              <span style={styles.burnSmallText}>
                {(pot * 0.02).toFixed(4)} SOL → $PP burn
              </span>
            </div>
          )}
        </div>

        {/* Your Hand */}
        <div style={styles.yourHand}>
          <div style={styles.playerLabel}>
            Your Hand
            <span style={styles.addr}>{wallet.publicKey?.toString().slice(0,8)}...</span>
          </div>
          <div style={styles.cards}>
            {myHand.map((card, i) => (
              <div 
                key={i} 
                style={{
                  ...styles.card,
                  ...styles.yourCard,
                  ...(isRed(card) ? styles.redCard : {}),
                }}
              >
                {getCardDisplay(card)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        {gameData.status?.dealing && !committed && (
          <button onClick={commitHand} disabled={loading} style={styles.actionBtn}>
            {loading ? 'Committing...' : 'Ready (Commit Hand)'}
          </button>
        )}

        {gameData.status?.betting && (
          <div style={styles.bettingControls}>
            <div style={styles.betInput}>
              <input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(e.target.value)}
                style={styles.input}
                step="0.01"
              />
              <button onClick={placeBet} disabled={loading} style={styles.betBtn}>
                {loading ? 'Betting...' : 'Bet'}
              </button>
            </div>
            <button onClick={fold} disabled={loading} style={styles.foldBtn}>
              Fold
            </button>
          </div>
        )}

        {gameData.status?.finished && isWinner && gameData.pot?.toNumber() > 0 && (
          <div style={styles.claimSection}>
            <button onClick={claimWinnings} disabled={loading} style={styles.claimBtn}>
              🎉 Claim {(pot * 0.98).toFixed(4)} SOL
            </button>
            <div style={styles.burnSettlement}>
              <span style={styles.burnFireSmall}>🔥</span>
              <span style={styles.burnSettleText}>
                {(pot * 0.02).toFixed(4)} SOL used to buy & burn $PP
              </span>
            </div>
          </div>
        )}

        {gameData.status?.finished && (
          <div style={styles.result}>
            {isWinner ? '🎉 You Won!' : '😔 You Lost'}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backBtn: {
    padding: '0.5rem 1rem',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '4px',
    color: '#fff',
    cursor: 'pointer',
  },
  status: {
    padding: '0.5rem 1rem',
    background: 'rgba(102,126,234,0.2)',
    borderRadius: '4px',
    color: '#667eea',
  },
  table: {
    background: 'radial-gradient(ellipse at center, #2d5016 0%, #1a2e0b 100%)',
    borderRadius: '150px',
    padding: '3rem',
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '8px solid #8b7355',
    boxShadow: 'inset 0 0 50px rgba(0,0,0,0.3)',
  },
  opponent: {
    textAlign: 'center',
  },
  yourHand: {
    textAlign: 'center',
  },
  playerLabel: {
    marginBottom: '0.5rem',
    fontSize: '0.9rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  addr: {
    fontSize: '0.75rem',
    color: '#aaa',
  },
  cards: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'center',
  },
  card: {
    width: '60px',
    height: '84px',
    background: '#fff',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.4rem',
    fontWeight: 'bold',
    color: '#000',
    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
  },
  yourCard: {
    width: '70px',
    height: '98px',
    fontSize: '1.6rem',
  },
  cardBack: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
  },
  redCard: {
    color: '#c00',
  },
  community: {
    textAlign: 'center',
  },
  communityLabel: {
    marginBottom: '0.5rem',
    fontSize: '0.85rem',
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  communityCards: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'center',
  },
  pot: {
    textAlign: 'center',
    background: 'rgba(0,0,0,0.5)',
    padding: '1rem 2rem',
    borderRadius: '8px',
  },
  potLabel: {
    display: 'block',
    fontSize: '0.8rem',
    color: '#888',
    marginBottom: '0.25rem',
  },
  potAmount: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#4caf50',
  },
  controls: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  bettingControls: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
  betInput: {
    display: 'flex',
    gap: '0.5rem',
  },
  input: {
    width: '100px',
    padding: '0.75rem',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '1rem',
  },
  actionBtn: {
    padding: '1rem 2rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '4px',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '1rem',
  },
  betBtn: {
    padding: '0.75rem 1.5rem',
    background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
    border: 'none',
    borderRadius: '4px',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: '600',
  },
  foldBtn: {
    padding: '0.75rem 1.5rem',
    background: 'linear-gradient(135deg, #f44336 0%, #c62828 100%)',
    border: 'none',
    borderRadius: '4px',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: '600',
  },
  claimBtn: {
    padding: '1rem 2rem',
    background: 'linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)',
    border: 'none',
    borderRadius: '4px',
    color: '#000',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '1.1rem',
  },
  result: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    padding: '1rem',
  },
  burnLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.3rem',
    marginTop: '0.4rem',
  },
  burnFireSmall: {
    fontSize: '0.85rem',
  },
  burnSmallText: {
    fontSize: '0.7rem',
    color: '#ff9800',
    fontWeight: '600',
  },
  claimSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
  },
  burnSettlement: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.4rem 1rem',
    background: 'rgba(255,107,53,0.1)',
    border: '1px solid rgba(255,107,53,0.2)',
    borderRadius: '6px',
  },
  burnSettleText: {
    fontSize: '0.75rem',
    color: '#ff9800',
    fontWeight: '600',
  },
};

export default PokerTable;
